"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { analytics } from "lib/segment";
import { create } from "zustand";
import { addVisitedPageToLocalStorage, getUtmParams, storeUtmParams, getStoredUtmParams } from "utils/analytics";
import { ViewedExperiments } from "app/api/get-active-experiments-on-page/route";

type ExperimentsViewedState = {
  viewedExperiments: ViewedExperiments;
  setViewedExperiments: (viewedExperimentsOnPage: ViewedExperiments) => void;
};

export const useExperimentsStore = create<ExperimentsViewedState>((set) => ({
  viewedExperiments: {},
  setViewedExperiments: (viewedExperimentsOnPage: ViewedExperiments) =>
    set((state) => ({ viewedExperiments: { ...state.viewedExperiments, ...viewedExperimentsOnPage } })),
}));

export function NavigationEvents() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setViewedExperiments } = useExperimentsStore();

  useEffect(() => {
    analytics?.page();

    // Handle UTM parameters
    const currentUtmParams = getUtmParams(searchParams);
    const storedUtmParams = getStoredUtmParams();

    // If we have new UTM params, store them
    if (
      currentUtmParams.utm_source ||
      currentUtmParams.utm_medium ||
      currentUtmParams.utm_campaign ||
      currentUtmParams.utm_term
    ) {
      storeUtmParams(currentUtmParams);
    }

    // Use either current or stored UTM params for analytics
    const utmParams = currentUtmParams.utm_source ? currentUtmParams : storedUtmParams;

    // collect all visited pages
    const visitedPages = addVisitedPageToLocalStorage(pathname);

    analytics?.identify({
      visited_pages: visitedPages,
      ...utmParams,
    });

    // Track specific page events
    if (pathname === "/pricing") {
      analytics?.track("Viewed Pricing Page", {
        page: pathname,
        timestamp: new Date().toISOString(),
        ...utmParams,
      });
    } else if (pathname === "/workflows") {
      analytics?.track("Viewed Workflows Page", {
        page: pathname,
        timestamp: new Date().toISOString(),
        ...utmParams,
      });
    }

    fetch("/api/get-active-experiments-on-page?pathname=" + encodeURIComponent(pathname)).then((res) => {
      if (res.status === 200) {
        return res.json().then((data) => {
          setViewedExperiments(data);
          for (const [experimentId, variant] of Object.entries(data)) {
            analytics?.track("Experiment Viewed", {
              experimentId,
              variant,
              ...utmParams,
            });
            analytics?.identify({ ["exp_" + experimentId]: variant });
          }
        });
      }
    });
  }, [pathname, searchParams, setViewedExperiments]);

  return null;
}
